import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { organizationDataAtom } from '@/store';
import { PlanType } from '@/lib/types';
import { PaymentUpgradeModal } from '@/components/billing';

interface UsePaymentGateOptions {
  source: string; // For analytics tracking (e.g., 'campaign_deploy', 'competitor_report')
  returnUrl?: string; // Optional custom return URL
}

interface UsePaymentGateReturn {
  PaymentGate: React.ComponentType; // Modal component to render
  requirePayment: <T extends any[], R>(
    action: (...args: T) => R,
    ...args: T
  ) => R | void; // Wrapper function for your action
  checkPayment: () => boolean; // Check if payment is required (true = payment needed)
}

/**
 * Custom hook to handle payment gating for premium features
 *
 * Usage 1 - Simple frontend check:
 * ```tsx
 * const { PaymentGate, requirePayment } = usePaymentGate({ source: 'campaign_deploy' });
 *
 * const handleDeploy = () => {
 *   requirePayment(() => deployMutation.mutate(campaignSlug));
 * };
 *
 * return (
 *   <>
 *     <Button onClick={handleDeploy}>Go Live</Button>
 *     <PaymentGate />
 *   </>
 * );
 * ```
 *
 * Usage 2 - Manual check with mutation error handling:
 * ```tsx
 * const { PaymentGate, checkPayment } = usePaymentGate({ source: 'campaign_deploy' });
 *
 * const mutation = useMutation({
 *   mutationFn: deployCampaign,
 *   onError: (error: any) => {
 *     if (error.response?.status === 402) {
 *       // Backend returned payment required
 *       // Modal will auto-retry on success
 *     }
 *   }
 * });
 *
 * const handleDeploy = () => {
 *   if (checkPayment()) {
 *     // Show modal immediately for better UX
 *     return;
 *   }
 *   mutation.mutate(campaignSlug);
 * };
 * ```
 */
export const usePaymentGate = ({
  source,
  returnUrl
}: UsePaymentGateOptions): UsePaymentGateReturn => {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const organization = useAtomValue(organizationDataAtom);
  const queryClient = useQueryClient();

  /**
   * Check if payment is required (returns true if user needs to pay)
   */
  const checkPayment = useCallback((): boolean => {
    return organization?.plan_type === PlanType.FREE;
  }, [organization?.plan_type]);

  /**
   * Wraps an action and checks if payment is required
   * If user is FREE, shows payment modal and stores action for retry
   * If user is PAID, executes action immediately
   */
  const requirePayment = useCallback(<T extends any[], R>(
    action: (...args: T) => R,
    ...args: T
  ): R | void => {
    // Check if user needs to upgrade
    if (checkPayment()) {
      // Store the action to retry after payment
      setPendingAction(() => () => action(...args));
      setShowPaymentModal(true);
      return;
    }

    // User has paid plan, execute immediately
    return action(...args);
  }, [checkPayment]);

  /**
   * Handle successful payment
   * Refreshes organization data and retries the pending action
   */
  const handlePaymentSuccess = useCallback(() => {
    // Refresh organization data to get new plan_type
    queryClient.invalidateQueries({ queryKey: ['organizations'] });

    // Retry the pending action
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  }, [queryClient, pendingAction]);

  /**
   * Handle modal close
   */
  const handleClose = useCallback(() => {
    setShowPaymentModal(false);
    setPendingAction(null);
  }, []);

  /**
   * Payment modal component
   */
  const PaymentGate = useCallback(() => (
    <PaymentUpgradeModal
      isOpen={showPaymentModal}
      onClose={handleClose}
      onSuccess={handlePaymentSuccess}
      returnUrl={returnUrl}
      source={source}
    />
  ), [showPaymentModal, handleClose, handlePaymentSuccess, returnUrl, source]);

  return {
    PaymentGate,
    requirePayment,
    checkPayment
  };
};
